import Axios from 'axios';
import { cleanRutFormat } from '../../utils';
import { useEffect, useRef, useState, useContext } from 'react';
import IdleTimer from 'react-idle-timer';
import { useHistory, useParams } from 'react-router-dom';
import BreadcrumbsModelDetail from '../components/BreadcrumbsModelDetail';
import AccessoriesSelector from '../components/ModelDetail/AccessoriesSelector';
import CarGallery from '../components/ModelDetail/CarGallery';
import ColorSelector from '../components/ModelDetail/ColorSelector';
import PaymentOptions from '../components/ModelDetail/PaymentOptions';
import PrepaidMaintenancePlanSelector from '../components/ModelDetail/PrepaidMaintenancePlanSelector';
import PurchaseTimeframeSelector from '../components/ModelDetail/PurchaseTimeframe';
import Summary from '../components/ModelDetail/Summary';
import UsadosSelector from '../components/ModelDetail/UsadosSelector';
import VersionPriceAndBonus from '../components/ModelDetail/VersionPriceAndBonus';
import VersionSelector from '../components/ModelDetail/VersionSelector';

import useConfiguratorData from '../hooks/useConfiguratorData';
import useFinanciadoData from '../hooks/useFinanciadoData';
import useLeadData from '../hooks/useLeadData';
import usePersonalData from '../hooks/usePersonalData';
import usePriceData from '../hooks/usePriceData';

import ModalContact from '../components/Modals/ModalContactMe';
import ModalInactiveUser from '../components/Modals/ModalInactivity';
import ModalPersonalData from '../components/Modals/ModalPersonalData';

import { ConfiguratorContext } from '../context';


const ModelDetail = () => {
    const { getSelectedModel, getSelectedVersion, getLoading, getVersionAndModelByVersionId, setSelectedModel, setSelectedVersion, setPreselectedDealerId, getInactiveModalUsed, setInactiveModalUsed } = useConfiguratorData();
    const { getSimulateNumberFees, getSimulateDownpaymentPercentage, setSimulatedCreditData, getPreApprovalData } = useFinanciadoData();
    const { getPersonalData, setPersonalData } = usePersonalData();
    const { getLeadId, updateLead } = useLeadData();
    const { getFinancedTotal } = usePriceData();

    const selectedModel = getSelectedModel();
    const selectedVersion = getSelectedVersion();
    const inactiveModalUsed = getInactiveModalUsed();
    const loading = getLoading();
    const { version_id } = useParams();
    const history = useHistory();
    
    const simulateDownpaymentPercentage = getSimulateDownpaymentPercentage();
    const simulateNumberFees = getSimulateNumberFees();
    const personalData = getPersonalData();
    const [isOpen, setIsOpen] = useState(true);
    const [modalContactIsOpen, setModalContactIsOpen] = useState(false);
    const [inactiveModalIsOpen, setInactiveModalIsOpen] = useState(false);
    const idleTimerRef = useRef(null);
    const idleSeconds = 90; //segundos de inactividad para mostrar el modal

    const [state] = useContext(ConfiguratorContext);
    const accessoriesEnabled = state.accessoriesEnabled;

    useEffect(() =>{
        const query = parseInt(new URLSearchParams(location.search).get('did'));
        setPreselectedDealerId(query);
    }, []);
    
    useEffect(() => {
        if(selectedVersion && getLeadId()) {
            simulateCredit()
        }
    }, [selectedVersion])

    const initPage = () => {
        if (version_id && !loading) {
            const { model, version } = getVersionAndModelByVersionId(parseInt(version_id));
            // si no devuelve una version no existe el id, vamos a la home
            if (!version || version.stock_warning) {
                history.replace('/');
            } else {
                setSelectedModel(model);
                setSelectedVersion(version);
            }
        }
    }

    useEffect(() => {
        if (getLeadId() && selectedModel && selectedVersion) {
            updateLead();
        }
    }, [selectedVersion]);
    
    useEffect(() => {
        window.scrollTo(0, 0);
        initPage();
    }, []);

    useEffect(() => {
        initPage();
    }, [loading, version_id]);

    const onIdle = () => {
        setInactiveModalIsOpen(true)
    }

    const onCloseModalContactMe = (contactMe) => {
        setInactiveModalIsOpen(false);
        setInactiveModalUsed(true)
        if(contactMe){
            setModalContactIsOpen(true);
        }
    }

    const simulateCredit = () => {
        const cleanRut = cleanRutFormat(getPersonalData().rut);
        Axios.post(`/api/vehicles/bnp/simulate_credit/`, {
            // v1:
            car_amount: getFinancedTotal(true),
            downpayment_percentage: parseFloat(simulateDownpaymentPercentage),
            number_fees: parseInt(simulateNumberFees),
            smart_buy: true,
            sap_code: selectedVersion.sap_id ? selectedVersion.sap_id : 0,
            full_name: getPersonalData().full_name,
            email: getPersonalData().email,
            rut: cleanRut,
            phone_number: getPersonalData().phone_number,
            // v2: 
            vehicleAmount: getFinancedTotal(true),
            downpayment_percentage: parseFloat(simulateDownpaymentPercentage),
            number_fees: parseInt(simulateNumberFees),
            sap_code: selectedVersion.sap_id ? selectedVersion.sap_id : 0,
            full_name: `${getPersonalData().first_name} ${getPersonalData().last_name}`,
            first_name: getPersonalData().first_name,
            last_name: getPersonalData().last_name,
            email: getPersonalData().email,
            clientRut: cleanRut,
            phone: getPersonalData().phone_number,
            downPayment: Math.round((simulateDownpaymentPercentage / 100) * getFinancedTotal(true)),
            creditAmount: getFinancedTotal(true) - Math.round((simulateDownpaymentPercentage / 100) * getFinancedTotal(true)),

        }).then(response => {
            let data = {...response.data};
            setSimulatedCreditData(data);
            getPreApprovalData().total_cost = data.credit_total_cost || data.totalCost;
            getPreApprovalData().request_id = data.simulationId;
            getPreApprovalData().rate = data.rate;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
        });
    }

    return (
        <>
        {!loading && selectedVersion ? 
            <>
                {!inactiveModalUsed && !isOpen ? 
                    <IdleTimer
                        ref={idleTimerRef}
                        timeout={idleSeconds * 1000}
                        onIdle={onIdle}
                    />
                : null}
                <ModalInactiveUser isOpen={inactiveModalIsOpen} onCloseModal={(contactMe) => {onCloseModalContactMe(contactMe)}}/>
                <ModalContact isOpen={modalContactIsOpen} onCloseModal={() => setModalContactIsOpen(false)} />
                <BreadcrumbsModelDetail model/>
                <ModalPersonalData isOpen={isOpen} onCloseModal={() => setIsOpen(false)} selectedModel={selectedModel} selectedVersion={selectedVersion}/>

                <div className="full-viewport-width model-detail">
                    <div className="main-container model-detail-container">
                        <div className="left-section">
                            <CarGallery />
                        </div>
                        <div className="right-section">
                            <VersionPriceAndBonus model={selectedModel} version={selectedVersion} />
                            <VersionSelector />
                            <ColorSelector />
                            <PrepaidMaintenancePlanSelector />
                            {
                                accessoriesEnabled && <AccessoriesSelector />
                            }
                            <PaymentOptions />
                            <UsadosSelector />
                            <PurchaseTimeframeSelector />
                            <Summary modalContactMeIsOpen={modalContactIsOpen} />
                        </div>
                    </div>
                </div>
            </>
        : 
            <div className="spinner-container">
                <div className="spinner"></div>
            </div>
        }
        </>
    );
}

export default ModelDetail;
